import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { PencilIcon, PlusIcon, TrashIcon } from '@/components/catalog/icons';
import { DomainHealthBadges, DomainHealthPanel } from '@/components/marketing/email-accounts/DomainHealthPanel';
import { EmailAccountFormModal } from '@/components/marketing/email-accounts/EmailAccountFormModal';
import { EmailAccountTestDialog } from '@/components/marketing/email-accounts/EmailAccountTestDialog';
import {
  ACCOUNT_IN_USE_CODE,
  IMAP_FAILED_CODE,
  staffLabel,
} from '@/components/marketing/email-accounts/email-account-rules';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Modal,
  PageHeader,
  Panel,
  PanelHeader,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { EmailLink } from '@/components/ui/TextLink';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { formatDateTime } from '@/lib/format';
import type { DomainHealth, MarketingEmailAccount } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';

/*
 * Marketing → Email accounts. `GET /marketing/email-accounts`, unpaginated.
 *
 * Shared SMTP accounts campaigns send through, each optionally restricted to
 * named users, each throttled per day and per minute, each with an optional
 * IMAP mailbox the inbox worker polls for bounces and replies.
 *
 * ⚠️ **THE ROUTE CARRIES NO GUARD**, so a reader without
 * `marketing-email-accounts.read` reaches this component and meets its own
 * refusal panel — and issues NO request, because an empty table would read as
 * "no accounts are set up", which is a claim about the business rather than
 * about their grant.
 *
 * ⛔ Passwords are never returned by the API and never rendered here; the list
 * knows only whether one is stored.
 */

const MODULE = 'marketing-email-accounts' as const;
const SKELETON_ROWS = 3;

/** Column floors in px, paired with the `w-*` class on each header. */
const TABLE_MIN_WIDTH =
  220 + // Account (fluid floor)
  176 + // SMTP          w-44
  128 + // Today         w-32
  176 + // IMAP          w-44
  160 + // Domain        w-40
  144 + // Last test     w-36
  128 + // Status        w-32
  288; // Actions        w-72
const TABLE_STYLE = { minWidth: `${TABLE_MIN_WIDTH}px` };

type RowNotice = { accountId: number; tone: 'success' | 'danger'; title: string; lines: string[] };

function MailIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );
}

function errorText(caught: unknown, fallback: string): string {
  if (caught instanceof ApiError) return caught.formMessage ?? caught.message ?? fallback;
  return fallback;
}

/* ── Cells ─────────────────────────────────────────────────────────────────── */

function SenderCell({ account }: { account: MarketingEmailAccount }) {
  return (
    <div className="min-w-0">
      <p className="m-0 text-sm font-medium text-fg [overflow-wrap:anywhere]">{account.name}</p>
      <p className="mt-0.5 mb-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
        {account.fromName} &lt;<EmailLink address={account.fromEmail} tone="muted" />&gt;
      </p>
    </div>
  );
}

function UsageCell({ account }: { account: MarketingEmailAccount }) {
  const atLimit = account.sentToday >= account.dailyLimit;
  return (
    <div className="grid gap-0.5 tabular">
      <span
        className={cx('text-sm', atLimit ? 'font-medium text-warning' : 'text-fg-secondary')}
        title={atLimit ? 'Today’s limit is reached; further sends roll to the next UTC day.' : undefined}
      >
        {account.sentToday.toLocaleString()} / {account.dailyLimit.toLocaleString()}
      </span>
      <span className="text-xs text-fg-muted">{account.perMinuteLimit} per minute</span>
    </div>
  );
}

function ImapCell({ account }: { account: MarketingEmailAccount }) {
  if (account.imapHost === null) {
    return <span className="text-xs text-fg-subtle">Not configured</span>;
  }
  return (
    <div className="grid min-w-0 gap-0.5">
      <span className="text-xs text-fg-secondary [overflow-wrap:anywhere]">{account.imapHost}</span>
      <span className="text-xs text-fg-muted tabular">
        {account.imapLastPolledAt ? `Polled ${formatDateTime(account.imapLastPolledAt)}` : 'Not polled yet'}
      </span>
      {account.imapLastError && (
        <span className="text-xs text-danger [overflow-wrap:anywhere]" title={account.imapLastError}>
          Last poll failed
        </span>
      )}
    </div>
  );
}

function LastTestCell({ account }: { account: MarketingEmailAccount }) {
  if (account.lastTest === null) return <span className="text-xs text-fg-subtle">Never tested</span>;
  return (
    <div className="grid gap-0.5">
      <Badge
        tone={account.lastTest.ok ? 'success' : 'danger'}
        dot
        title={account.lastTest.error ?? undefined}
      >
        {account.lastTest.ok ? 'Passed' : 'Failed'}
      </Badge>
      <span className="text-xs text-fg-muted tabular">{formatDateTime(account.lastTest.at)}</span>
    </div>
  );
}

function StatusCell({ account }: { account: MarketingEmailAccount }) {
  const names = account.allowedUsers.map(staffLabel).join(', ');
  return (
    <div className="flex flex-wrap gap-1">
      <Badge tone={account.isActive ? 'success' : 'neutral'} dot>
        {account.isActive ? 'Active' : 'Inactive'}
      </Badge>
      {account.restricted && (
        <Badge tone="neutral" variant="outline" title={names === '' ? 'No users named' : names}>
          Restricted · {account.allowedUsers.length}
        </Badge>
      )}
    </div>
  );
}

/* ── Page ─────────────────────────────────────────────────────────────────── */

export function EmailAccountsPage() {
  const { ability } = useAuth();
  const canRead = ability.canRead(MODULE);
  const canCreate = ability.canCreate(MODULE);
  const canUpdate = ability.canUpdate(MODULE);
  const canDelete = ability.canDelete(MODULE);

  const [accounts, setAccounts] = useState<MarketingEmailAccount[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState<string | null>(null);

  const [formState, setFormState] = useState<{ account?: MarketingEmailAccount } | null>(null);
  const [testing, setTesting] = useState<MarketingEmailAccount | null>(null);
  const [notice, setNotice] = useState<RowNotice | null>(null);
  const [imapBusy, setImapBusy] = useState<number | null>(null);

  const [healthFor, setHealthFor] = useState<MarketingEmailAccount | null>(null);
  const [healthResult, setHealthResult] = useState<{ health: DomainHealth; checkedAt: string | null } | null>(null);
  const [healthBusy, setHealthBusy] = useState(false);
  const [healthError, setHealthError] = useState<string | null>(null);

  const [pendingDelete, setPendingDelete] = useState<MarketingEmailAccount | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteBlocked, setDeleteBlocked] = useState(false);

  /*
   * `loading` is true only until the first answer: a refresh after an action
   * keeps the rows on screen rather than fading them, and setting state
   * synchronously from the mount effect is what the linter refuses.
   */
  const load = useCallback((signal?: AbortSignal) => {
    return api
      .listMarketingEmailAccounts(signal)
      .then((response) => {
        setAccounts(response.data);
        setError(null);
        setForbidden(null);
      })
      .catch((caught: unknown) => {
        if (signal?.aborted) return;
        if (caught instanceof ApiError && caught.status === 403) {
          setForbidden(caught.message || 'Your account is not allowed to read email accounts.');
          setAccounts(null);
          setError(null);
          return;
        }
        setForbidden(null);
        setError(caught instanceof Error ? caught.message : 'Failed to load email accounts');
      })
      .finally(() => {
        if (!signal?.aborted) setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!canRead) return;
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [canRead, load]);

  async function runImapTest(account: MarketingEmailAccount) {
    setImapBusy(account.id);
    setNotice(null);
    try {
      const response = await api.testMarketingEmailAccountImap(account.id);
      const result = response.data;
      setNotice({
        accountId: account.id,
        tone: 'success',
        title: `${account.name}: signed in to the IMAP mailbox.`,
        lines: [`${result.mailbox} holds ${result.messages.toLocaleString()} ${result.messages === 1 ? 'message' : 'messages'}.`],
      });
    } catch (caught) {
      setNotice({
        accountId: account.id,
        tone: 'danger',
        title:
          caught instanceof ApiError && caught.code === IMAP_FAILED_CODE
            ? `${account.name}: ${caught.message || 'the IMAP server refused the connection.'}`
            : `${account.name}: ${errorText(caught, 'could not reach the server.')}`,
        lines: caught instanceof ApiError ? caught.detailReasons : [],
      });
    } finally {
      setImapBusy(null);
      void load();
    }
  }

  function openHealth(account: MarketingEmailAccount) {
    setHealthFor(account);
    setHealthError(null);
    setHealthResult(account.health ? { health: account.health, checkedAt: account.healthCheckedAt } : null);
  }

  async function runHealthCheck() {
    if (!healthFor) return;
    setHealthBusy(true);
    setHealthError(null);
    try {
      const response = await api.checkMarketingEmailAccountHealth(healthFor.id);
      setHealthResult({ health: response.data, checkedAt: new Date().toISOString() });
      void load();
    } catch (caught) {
      setHealthError(errorText(caught, 'Could not reach the server. The check did not run.'));
    } finally {
      setHealthBusy(false);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteMarketingEmailAccount(pendingDelete.id);
      setPendingDelete(null);
      void load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === ACCOUNT_IN_USE_CODE) {
        setDeleteBlocked(true);
        setDeleteError(
          `${caught.message} A campaign uses this account, so it cannot be deleted. Edit it and untick Active to stop it being offered instead.`,
        );
      } else {
        setDeleteError(errorText(caught, 'Could not reach the server. The account has NOT been deleted.'));
      }
    } finally {
      setDeleting(false);
    }
  }

  /* ── Refusal ─────────────────────────────────────────────────────────── */

  if (!canRead) {
    return (
      <div className="grid min-w-0 gap-4">
        <PageHeader eyebrow="B2B Marketing" title="Email accounts" />
        <Panel aria-labelledby="email-accounts-denied-heading">
          <PanelHeader id="email-accounts-denied-heading" title="Email accounts" />
          <EmptyState
            icon={<MailIcon />}
            title="Not available to your account"
            description="Your permission set does not include this. An administrator can change that."
          />
        </Panel>
      </div>
    );
  }

  const rows = accounts ?? [];
  const initialLoad = loading && accounts === null;
  const countLabel = initialLoad
    ? 'Loading…'
    : accounts === null
      ? null
      : `${rows.length} ${rows.length === 1 ? 'account' : 'accounts'}`;

  function renderActions(account: MarketingEmailAccount, compact: boolean): ReactNode {
    return (
      <div className={cx('flex flex-wrap gap-1.5', compact ? 'justify-start' : 'justify-end')}>
        {canUpdate && (
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            leadingIcon={<PencilIcon />}
            aria-label={`Edit ${account.name}`}
            title={`Edit ${account.name}`}
            onClick={() => setFormState({ account })}
          />
        )}
        {canUpdate && (
          <Button variant="secondary" size="sm" onClick={() => setTesting(account)}>
            Send test
          </Button>
        )}
        {canUpdate && account.imapHost !== null && (
          <Button
            variant="secondary"
            size="sm"
            loading={imapBusy === account.id}
            onClick={() => void runImapTest(account)}
          >
            Test IMAP
          </Button>
        )}
        {(canUpdate || account.health !== null) && (
          <Button variant="secondary" size="sm" onClick={() => openHealth(account)}>
            Domain health
          </Button>
        )}
        {canDelete && (
          <Button
            variant="danger"
            size="sm"
            iconOnly
            leadingIcon={<TrashIcon />}
            aria-label={`Delete ${account.name}`}
            title={`Delete ${account.name}`}
            onClick={() => {
              setDeleteError(null);
              setDeleteBlocked(false);
              setPendingDelete(account);
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="grid min-w-0 gap-4">
      <PageHeader
        eyebrow="B2B Marketing"
        title="Email accounts"
        actions={
          canCreate ? (
            <Button variant="primary" leadingIcon={<PlusIcon />} onClick={() => setFormState({})}>
              Add account
            </Button>
          ) : undefined
        }
      />

      <Panel className="min-w-0" aria-labelledby="email-accounts-heading">
        <PanelHeader id="email-accounts-heading" title="Sending accounts" description={countLabel ?? undefined} />

        {countLabel && (
          <p role="status" aria-live="polite" className="sr-only">
            {countLabel}
          </p>
        )}

        {notice && (
          <div className="border-b border-border px-4 py-3">
            <Notice
              tone={notice.tone === 'danger' ? 'danger' : 'success'}
              role={notice.tone === 'danger' ? 'alert' : 'status'}
              title={<span className="[overflow-wrap:anywhere]">{notice.title}</span>}
              action={
                <Button variant="ghost" size="sm" onClick={() => setNotice(null)}>
                  Dismiss
                </Button>
              }
            >
              {notice.lines.length > 0 && (
                <div className="grid min-w-0 gap-1">
                  {notice.lines.map((line) => (
                    <code key={line} className="block [overflow-wrap:anywhere]">
                      {line}
                    </code>
                  ))}
                </div>
              )}
            </Notice>
          </div>
        )}

        {forbidden && (
          <div className="border-b border-border px-4 py-3">
            <Notice tone="danger" role="alert" title="Your account can no longer read email accounts.">
              <p className="m-0 [overflow-wrap:anywhere]">{forbidden}</p>
            </Notice>
          </div>
        )}

        {error && (
          <div className="border-b border-border px-4 py-3">
            <Notice tone="danger" role="alert" title={<>Couldn&rsquo;t load email accounts.</>}>
              <span className="min-w-0 [overflow-wrap:anywhere]">{error}</span>
            </Notice>
          </div>
        )}

        {!forbidden && (
          <div
            aria-busy={loading || undefined}
            className={cx(
              'min-w-0 transition-opacity duration-150 ease-out motion-reduce:transition-none',
              loading && accounts && 'opacity-60',
            )}
          >
            {initialLoad ? (
              <>
                <div className="hidden md:block">
                  <TableWrap frozenHeader>
                    <Table style={TABLE_STYLE}>
                      <AccountsTableHead />
                      <TBody>
                        {Array.from({ length: SKELETON_ROWS }, (_, i) => (
                          <TR key={i}>
                            <TD>
                              <div className="grid gap-1.5">
                                <Skeleton className="h-3 w-36" />
                                <Skeleton className="h-2.5 w-48" />
                              </div>
                            </TD>
                            <TD><Skeleton className="h-3 w-28" /></TD>
                            <TD><Skeleton className="h-3 w-16" /></TD>
                            <TD><Skeleton className="h-3 w-28" /></TD>
                            <TD><Skeleton className="h-4 w-28" /></TD>
                            <TD><Skeleton className="h-4 w-16" /></TD>
                            <TD><Skeleton className="h-4 w-16" /></TD>
                            <TD numeric pinned>
                              <div className="flex justify-end gap-1.5">
                                <Skeleton className="h-6 w-20" />
                                <Skeleton className="h-6 w-24" />
                              </div>
                            </TD>
                          </TR>
                        ))}
                      </TBody>
                    </Table>
                  </TableWrap>
                </div>
                <ul className="m-0 grid list-none gap-3 p-4 md:hidden">
                  {Array.from({ length: SKELETON_ROWS }, (_, i) => (
                    <li key={i} className="grid gap-2 rounded-md border border-border p-3">
                      <Skeleton className="h-3 w-40" />
                      <Skeleton className="h-2.5 w-56" />
                      <Skeleton className="h-4 w-32" />
                    </li>
                  ))}
                </ul>
              </>
            ) : rows.length === 0 ? (
              error ? (
                <EmptyState
                  icon={<MailIcon />}
                  title="Nothing to show"
                  description="Couldn’t load the list — see the message above."
                />
              ) : (
                <EmptyState
                  icon={<MailIcon />}
                  title="No email accounts yet"
                  description="Campaigns cannot send until an account is added."
                  action={
                    canCreate ? (
                      <Button variant="primary" size="sm" leadingIcon={<PlusIcon />} onClick={() => setFormState({})}>
                        Add account
                      </Button>
                    ) : undefined
                  }
                />
              )
            ) : (
              <>
                <div className="hidden md:block">
                  <TableWrap frozenHeader>
                    <Table style={TABLE_STYLE}>
                      <AccountsTableHead />
                      <TBody>
                        {rows.map((account) => (
                          <TR key={account.id} interactive>
                            <TD>
                              <SenderCell account={account} />
                            </TD>
                            <TD>
                              <span className="text-xs text-fg-secondary [overflow-wrap:anywhere]">
                                {account.smtpHost}:{account.smtpPort}
                              </span>
                            </TD>
                            <TD>
                              <UsageCell account={account} />
                            </TD>
                            <TD>
                              <ImapCell account={account} />
                            </TD>
                            <TD>
                              <DomainHealthBadges health={account.health} />
                            </TD>
                            <TD>
                              <LastTestCell account={account} />
                            </TD>
                            <TD>
                              <StatusCell account={account} />
                            </TD>
                            {/* Pinned: the 1420px floor puts this lane off-screen at
                                1440, hiding Send test / Domain health / Delete. */}
                            <TD numeric pinned>{renderActions(account, false)}</TD>
                          </TR>
                        ))}
                      </TBody>
                    </Table>
                  </TableWrap>
                </div>
                <ul className="m-0 grid list-none gap-3 p-4 md:hidden">
                  {rows.map((account) => (
                    <li key={account.id} className="grid min-w-0 gap-3 rounded-md border border-border p-3">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <SenderCell account={account} />
                        <StatusCell account={account} />
                      </div>
                      <dl className="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-2 text-xs">
                        <dt className="text-fg-muted">SMTP</dt>
                        <dd className="m-0 text-fg-secondary [overflow-wrap:anywhere]">
                          {account.smtpHost}:{account.smtpPort}
                        </dd>
                        <dt className="text-fg-muted">Today</dt>
                        <dd className="m-0">
                          <UsageCell account={account} />
                        </dd>
                        <dt className="text-fg-muted">IMAP</dt>
                        <dd className="m-0">
                          <ImapCell account={account} />
                        </dd>
                        <dt className="text-fg-muted">Domain</dt>
                        <dd className="m-0">
                          <DomainHealthBadges health={account.health} />
                        </dd>
                        <dt className="text-fg-muted">Last test</dt>
                        <dd className="m-0">
                          <LastTestCell account={account} />
                        </dd>
                      </dl>
                      {renderActions(account, true)}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </Panel>

      <EmailAccountFormModal
        open={formState !== null}
        onClose={() => setFormState(null)}
        account={formState?.account}
        onSaved={() => void load()}
      />

      <EmailAccountTestDialog account={testing} onClose={() => setTesting(null)} onTested={() => void load()} />

      <Modal
        open={healthFor !== null}
        onClose={() => setHealthFor(null)}
        size="md"
        title={healthFor ? `Domain health — ${healthFor.name}` : 'Domain health'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setHealthFor(null)} disabled={healthBusy}>
              Close
            </Button>
            {canUpdate && (
              <Button variant="primary" loading={healthBusy} onClick={() => void runHealthCheck()}>
                {healthResult ? 'Run check again' : 'Run health check'}
              </Button>
            )}
          </>
        }
      >
        <div className="grid gap-3">
          {healthError && (
            <Notice tone="danger" role="alert" className="[overflow-wrap:anywhere]">
              {healthError}
            </Notice>
          )}
          {healthResult ? (
            <DomainHealthPanel health={healthResult.health} checkedAt={healthResult.checkedAt} />
          ) : healthBusy ? (
            <div className="grid gap-2">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : (
            <p className="m-0 text-sm text-fg-secondary">This account’s domain has not been checked yet.</p>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => {
          setPendingDelete(null);
          setDeleteError(null);
          setDeleteBlocked(false);
        }}
        onConfirm={() => void confirmDelete()}
        tone="danger"
        title={pendingDelete ? `Delete ${pendingDelete.name}?` : 'Delete email account?'}
        description={
          pendingDelete ? (
            <p className="m-0 [overflow-wrap:anywhere]">
              {pendingDelete.fromEmail} stops being available for sending, and its stored SMTP and IMAP
              passwords are removed. This cannot be undone.
            </p>
          ) : (
            ''
          )
        }
        confirmLabel="Delete account"
        loading={deleting}
        confirmDisabled={deleteBlocked}
        error={deleteError && <span className="[overflow-wrap:anywhere]">{deleteError}</span>}
      />
    </div>
  );
}

function AccountsTableHead() {
  return (
    <THead>
      <TR>
        <TH>Account</TH>
        <TH className="w-44">SMTP</TH>
        <TH className="w-32">Today</TH>
        <TH className="w-44">IMAP</TH>
        <TH className="w-40">Domain</TH>
        <TH className="w-36">Last test</TH>
        <TH className="w-32">Status</TH>
        <TH className="w-72" numeric pinned>
          Actions
        </TH>
      </TR>
    </THead>
  );
}
